// ── Ritual Analytics ─────────────────────────────────────────────────────────
// Google Analytics is only loaded after explicit user consent.
// No tracking script is injected until `loadAnalytics()` is called.

const GA_ID = "G-CH3FTFQ3P6";
const STORAGE_KEY = "ritual_cookie_consent";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

// ── Consent helpers ───────────────────────────────────────────────────────────

export type ConsentValue = "all" | "essentials" | null;

export const getConsent = (): ConsentValue => {
  try {
    const val = localStorage.getItem(STORAGE_KEY);
    if (val === "all" || val === "essentials") return val;
    return null;
  } catch {
    return null;
  }
};

export const setConsent = (value: "all" | "essentials"): void => {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch { /* noop */ }
};

// ── Analytics loader ──────────────────────────────────────────────────────────

export const loadAnalytics = (): void => {
  if (typeof window === "undefined") return;
  if (window.__GA_LOADED__) return;
  window.__GA_LOADED__ = true;

  // Inject the gtag script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  // Bootstrap dataLayer and gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args) {
    window.dataLayer.push(args);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: false }); // manual page_view via trackPageView
};

// ── On-startup check ─────────────────────────────────────────────────────────
// Call once at app init — respects a previously saved "all" consent.

export const initAnalytics = (): void => {
  if (getConsent() === "all") loadAnalytics();
};

// ── Event helpers ─────────────────────────────────────────────────────────────

export const trackEvent = (name: string, params?: Record<string, unknown>): void => {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params ?? {});
};

export const trackPageView = (path: string): void => {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_location: window.location.href,
    page_path: path,
  });
};

// Augment Window for the GA loaded flag
declare global {
  interface Window {
    __GA_LOADED__?: boolean;
  }
}
